import React from 'react';
import DatePicker from 'react-datepicker/dist';
import styled from '../styled';
import DatePickerInput from './DatePickerInput';
import DatePickerHeader from './DatePickerHeader';
import 'react-datepicker/dist/react-datepicker.css';
import { DATE_FORMATS } from 'src/constants/constants';
import { formatDate, toMoment } from 'src/helpers/date';

const styles = theme => ({
  root: {},
  overlay: {
    height: '0px',
  },
  '@global': {
    '.react-datepicker__tab-loop': {
      position: 'relative',
    },

    '.react-datepicker-popper': {
      zIndex: '996',
      transform: 'none !important',
      willChange: 'unset !important',
    },
    '.react-datepicker__day--outside-month': {
      visibility: 'hidden',
    },
    '.react-datepicker__day--selected, .react-datepicker__day--keyboard-selected': {
      borderRadius: '0.3rem',
      backgroundColor: '#2a87d0 !important',
      color: '#fff',
    },
    '.react-datepicker__day--today': {
      color: '#D0021B',
      backgroundColor: '#fff',
    },
    '.react-datepicker__day-names': {
      display: 'none',
    },
    '.react-datepicker__header--custom': {
      backgroundColor: '#24242E',
    },
    '.react-datepicker__day': {
      fontSize: '1rem',
      fontWeight: '400',
      fontFamily: 'EYInterstate, sans-serif',
      width: '2rem',
    },
    '.react-datepicker__month': {
      margin: '1rem',
    },
    '.react-datepicker-wrapper': {
      width: '100%',
    },
  },
});
class DatePickerBase extends React.Component {
  constructor(props) {
    super(props);
    // eslint-disable-next-line
    if (props.value != '') {
      const currentDate = this.getDateObj(props.value);
      this.state = {
        month: props.value && currentDate,
        displayMonth: props.value && currentDate,
      };
    } else {
      this.state = {
        month: null,
        displayMonth: '',
      };
    }
    this.datePickerRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      const newMonth = this.props.value ? this.getDateObj(this.props.value) : '';
      this.setState({ month: newMonth });
    }
  }

  getDateObj = value => {
    return toMoment(value).toDate();
  };

  formatMonth = date => {
    return toMoment(date).format(this.props.monthFormat);
  };

  clearDate = () => {
    this.setState({ month: '' }, () => this.props.onChange(''));
  };

  handleDateChange = month => {
    this.setState({ month });
    this.props.onChange(this.props.showMonthYearPicker ? this.formatMonth(month) : formatDate(month));
  };

  setDatePickerRef = ref => {
    this.datePickerRef.current = ref;
    if (this.props.setDatePickerRef) {
      this.props.setDatePickerRef(ref);
    }
  };

  handleMonthChange = month => {
    this.datePickerRef.current.setPreSelection(this.getDateObj(month));
  };

  handleYearChange = year => {
    !this.props.hideMonthDropdown && this.datePickerRef.current.setPreSelection(this.getDateObj(year));
    this.props.handleYearChange && this.props.handleYearChange(year);
  };

  render() {
    const {
      className,
      classes,
      onChange,
      from,
      to,
      defaultYear,
      value,
      selectedDate,
      monthRange,
      hideMonthDropdown,
      showMonthYearPicker,
      monthFormat,
      monthFormatToShow,
      placeholderMonth,
      onCalendarOpen,
      ...otherProps
    } = this.props;

    const dateFormat = monthFormat || DATE_FORMATS.DISPLAY_DATE_ONLY;
    const dateFormatToShow = monthFormatToShow || DATE_FORMATS.DATE_FORMAT_TO_SHOW;

    const CustomInput = props =>
      DatePickerInput(
        { ...otherProps, dateFormat, clearDate: this.clearDate, datePickerRef: this.datePickerRef, classes },
        { ...props, required: otherProps.required },
      );

    const getDateRange = () => {
      let range = {};
      // eslint-disable-next-line no-unused-expressions
      !!from ? (range['minDate'] = toMoment(from).toDate()) : '';
      // eslint-disable-next-line no-unused-expressions
      !!to ? (range['maxDate'] = toMoment(to).toDate()) : '';
      return range;
    };

    return (
      <DatePicker
        openToDate={this.state.month || new Date()}
        title={value}
        selected={this.state.month}
        onChange={date => this.handleDateChange(date)}
        onCalendarOpen={() => onCalendarOpen && onCalendarOpen()}
        onYearChange={year => this.handleYearChange(year)}
        onMonthChange={month => this.handleMonthChange(month)}
        customInput={<CustomInput />}
        ref={ref => this.setDatePickerRef(ref)}
        autoComplete="off"
        onClickOutside={() => {
          this.datePickerRef.current.setOpen(false);
        }}
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          increaseYear,
          decreaseYear,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <DatePickerHeader
            pickerRef={{
              date,
              changeYear,
              changeMonth,
              decreaseMonth,
              increaseMonth,
              increaseYear,
              decreaseYear,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled,
            }}
            from={from}
            to={to}
            monthRange={monthRange}
            defaultYear={defaultYear}
            value={this.state.month || ''}
            hideMonthDropdown={hideMonthDropdown}
            hideWeekDays={hideMonthDropdown}
          />
        )}
        dateFormat={dateFormatToShow}
        placeholderText={dateFormat}
        peekNextMonth
        showMonthDropdown={!hideMonthDropdown}
        showYearDropdown
        popperPlacement="bottom-start"
        inline={false}
        popperModifiers={{
          flip: { enabled: false }, // used to on/off the picker popup flip effect
        }}
        showPopperArrow={false}
        shouldCloseOnSelect={true}
        {...getDateRange()}
        showMonthYearPicker={showMonthYearPicker}
        strictParsing={true}
        chooseDayAriaLabelPrefix={''}
        disabledDayAriaLabelPrefix={''}
        adjustDateOnChange={true}
      />
    );
  }
}

export { DatePickerBase };
export default styled(DatePickerBase)(styles);
